﻿import {Injectable} from '@angular/core';
import * as moment from '@node_modules/moment';

export class ZaIdValidationResult {
    public isValid: boolean;
    public error?: string;
    public gender?: string;
    public dob?: Date;
    public isCitizen?: boolean;
}

export const ZaIdValidationErrors = Object.freeze({
    InvalidLength: 'ID number must be 13 digits long',
    InvalidCheckSum: 'The ID number provided is not valid'
});

@Injectable({
    providedIn: 'root'
})
export class ZaIdValidatorService {
    private id: string;

    constructor() {
    }

    public setId(id: string): void {
        this.id = id;
    }

    /**
     * Validates and parses the ID number.
     *
     * This produces a consolidated result with a validation status and either the error that occurred or the information parsed from the ID number.
     *
     * @function
     * @return The result object. Contains validity state and either error or parsed information.
     * @see {ZaIdValidationResult}
     * @example
     * const validIdNumber = '9001049818080';
     * const validator = new ZaIdValidator(validIdNumber);
     *
     * const result = validator.parse();
     * // {
     * //   isValid: true,
     * //   dob: new Date(1990, 0, 4),
     * //   gender: 'Male',
     * //   isCitizen: true
     * // }
     *
     * const invalidIdNumber = '9001049818081';
     * const validator = new ZaIdValidator(invalidIdNumber);
     * info = saIdParser.parse(invalidIdNumber);
     * // {
     * //   isValid: false,
     * //   error: 'The ID number provided is not valid'
     * // }
     */
    public parse(): ZaIdValidationResult {
        return this.validate();
    }

    /**
     * Parses out the date of birth for the ID number.
     * @private
     * @function
     * @return The date object of the date of birth
     * @see moment
     * @see Date
     */
    private dob(): Date {
        // TODO: remove reliance on dependency
        return moment(this.id.slice(0, 6), 'YYMMDD').toDate();
    }

    /**
     * Parses out the biological sex of the individual.
     * @private
     * @function
     * @return string representation of the individual's biological sex.
     */
    private gender(): string {
        return parseInt(this.id[6], 10) <= 4 ? 'Female' : 'Male';
    }

    /**
     * Parses the ID number to confirm if the individual is a South African citizen.
     * @private
     * @function
     * @return true or false
     */
    private isCitizen(): boolean {
        return parseInt(this.id[10], 10) === 0;
    }

    /**
     * Primary validation function for South African ID numbers.
     *
     * Initially confirms the length of the ID number meets the required length (13 digits). Followed by a full checksum test to confirm the validity of the ID number.
     * @private
     * @function
     * @return The result object. Contains validity state and either error or parsed information.
     * @see {ZaIdValidationResult}
     */
    private validate(): ZaIdValidationResult {
        const regexp = /^[0-9]{13}$/;

        const isCorrectLength = regexp.test(this.id);

        if (!isCorrectLength) {
            return <ZaIdValidationResult> {
                error: ZaIdValidationErrors.InvalidLength,
                isValid: false
            };
        }

        const checkDigit = parseInt(this.id[12], 10);

        const idArr = this.id.slice(0, 12).split('');

        const oddSum = idArr.reduce(this.oddSumReducer, 0);

        let evenString = idArr.reduce(this.evenStringReducer, '');

        evenString = (parseInt(evenString, 10) * 2) + '';

        const evenSum = evenString.split('').reduce(this.evenSumReducer, 0);

        const total = evenSum + oddSum;

        let computed = 10 - (total % 10);

        computed = computed === 10 ? 0 : computed;

        const isValid = computed === checkDigit;

        if (isValid) {
            return <ZaIdValidationResult> {
                isValid,
                dob: this.dob(),
                gender: this.gender(),
                isCitizen: this.isCitizen()
            };
        } else {
            return <ZaIdValidationResult>{
                isValid,
                error: ZaIdValidationErrors.InvalidCheckSum
            };
        }
    }

    /**
     * Reducer used to identify and sum the odd digits of the ID number.
     * @param sum The summed value up to this point.
     * @param value The current value to be added.
     * @param index The index of the value within the array.
     * @private
     */
    private oddSumReducer(sum: number, value: string, index: number): number {
        return sum += (index + 1) % 2 > 0 ? parseInt(value, 10) : 0;
    }

    /**
     * Reducer used to identify and concatenate the even digits of the ID number.
     * @param sum The concatenated string of values up to this point.
     * @param value The current value to be added.
     * @param index The index of the value within the array.
     * @private
     */
    private evenStringReducer(sum: string, value: string, index: number): string {
        return sum += (index + 1) % 2 === 0 ? value : '';
    }

    /**
     * Reducer user to sum the values provided.
     * @param sum The summed value up to this point.
     * @param value The current valueThe current value to be added.
     * @private
     */
    private evenSumReducer(sum: number, value: string): number {
        return sum + parseInt(value, 10);
    }
}
